export type TierName =
  | 'Bronze' | 'Silver' | 'Gold' | 'Platinum' | 'Diamond' | 'Elite' | 'Master' | 'Grandmaster' | 'Mythic' | 'Legend';

export interface TierConfig {
  level: number;
  name: TierName;
  bountiesRequired: number;
  /** Pay rate per approved bounty at this level (the ONLY earning event) */
  payRate: number;
  fill: string;
  border: string;
  text: string;
  glowClass: string;
  gradientFrom: string;
  gradientVia: string;
  gradientTo: string;
}

export const TIERS: TierConfig[] = [
  {
    level: 1,
    name: 'Bronze',
    bountiesRequired: 0,
    payRate: 20,
    fill: '#F4C7A1',
    border: '#E3A46A',
    text: '#A85E1C',
    glowClass: 'tier-glow-bronze',
    gradientFrom: '#451a03',
    gradientVia: '#78350f',
    gradientTo: '#92400e',
  },
  {
    level: 2,
    name: 'Silver',
    bountiesRequired: 1,
    payRate: 25,
    fill: '#E8E8E8',
    border: '#B0B8C4',
    text: '#4A5568',
    glowClass: 'tier-glow-silver',
    gradientFrom: '#1a1a2e',
    gradientVia: '#2d3748',
    gradientTo: '#4a5568',
  },
  {
    level: 3,
    name: 'Gold',
    bountiesRequired: 3,
    payRate: 30,
    fill: '#F7E39A',
    border: '#E2C458',
    text: '#9C7A10',
    glowClass: 'tier-glow-gold',
    gradientFrom: '#422006',
    gradientVia: '#713f12',
    gradientTo: '#92400e',
  },
  {
    level: 4,
    name: 'Platinum',
    bountiesRequired: 6,
    payRate: 35,
    fill: '#CDECF7',
    border: '#95D4E8',
    text: '#2F7C91',
    glowClass: 'tier-glow-platinum',
    gradientFrom: '#083344',
    gradientVia: '#164e63',
    gradientTo: '#0c4a6e',
  },
  {
    level: 5,
    name: 'Diamond',
    bountiesRequired: 10,
    payRate: 40,
    fill: '#A8C8F8',
    border: '#4A90D9',
    text: '#1A3A6B',
    glowClass: 'tier-glow-diamond',
    gradientFrom: '#0a0f2e',
    gradientVia: '#0d1f5c',
    gradientTo: '#1a3a8f',
  },
  {
    level: 6,
    name: 'Elite',
    bountiesRequired: 15,
    payRate: 50,
    fill: '#BFECCF',
    border: '#74C993',
    text: '#1F7A46',
    glowClass: 'tier-glow-emerald',
    gradientFrom: '#022c22',
    gradientVia: '#064e3b',
    gradientTo: '#065f46',
  },
  {
    level: 7,
    name: 'Master',
    bountiesRequired: 21,
    payRate: 60,
    fill: '#D8C9F7',
    border: '#A98AE8',
    text: '#5C3A9B',
    glowClass: 'tier-glow-master',
    gradientFrom: '#2e1065',
    gradientVia: '#4c1d95',
    gradientTo: '#5b21b6',
  },
  {
    level: 8,
    name: 'Grandmaster',
    bountiesRequired: 28,
    payRate: 70,
    fill: '#FFD580',
    border: '#E6A817',
    text: '#7A4500',
    glowClass: 'tier-glow-grandmaster',
    gradientFrom: '#3d1a00',
    gradientVia: '#7a3500',
    gradientTo: '#b35400',
  },
  {
    level: 9,
    name: 'Mythic',
    bountiesRequired: 36,
    payRate: 80,
    fill: '#F7C6E7',
    border: '#E67BC2',
    text: '#9C2F6A',
    glowClass: 'tier-glow-mythic',
    gradientFrom: '#500724',
    gradientVia: '#831843',
    gradientTo: '#9d174d',
  },
  {
    level: 10,
    name: 'Legend',
    bountiesRequired: 45,
    payRate: 100,
    fill: '#FFD5D5',
    border: '#E53535',
    text: '#9B1111',
    glowClass: 'tier-glow-legend',
    gradientFrom: '#450a0a',
    gradientVia: '#7f1d1d',
    gradientTo: '#991b1b',
  },
];

/** Get the tier config for a given approved-bounty count */
export function getTierForBounties(bounties: number): TierConfig {
  let tier = TIERS[0];
  for (const t of TIERS) {
    if (bounties >= t.bountiesRequired) tier = t;
    else break;
  }
  return tier;
}

/** Get the pay rate (in dollars) for a member with the given approved-bounty count */
export function getPayRateForBounties(bounties: number): number {
  return getTierForBounties(bounties).payRate;
}

/** @deprecated Use getTierForBounties instead */
export function getTierForXP(xp: number): TierConfig {
  return getTierForBounties(xp);
}

export function getNextTier(currentLevel: number): TierConfig | null {
  return TIERS.find((t) => t.level === currentLevel + 1) ?? null;
}